import { CartItem } from "../typings/interfaces/pos/cart-item";
import { PaymentTypeEnum } from "../typings/enum/paymentTypes";
import { ITrayPrintCustomizationLine } from "../typings/interfaces/print/ticket-print-request";

// Pure formatting helpers shared by every print-request builder (check-in's
// payment-modal.tsx, pickup's PickupReceiptModal.tsx, reprint's
// ticket-print.ts). Deliberately kept dependency-free (no redux action
// creators, no axios) so a component that only needs to format a print
// payload - PickupReceiptModal.tsx included - doesn't have to drag in
// data-fetching/HTTP machinery it never uses just to reach these.

// Shared "first letter of each word capitalized, rest lowercase" convention
// for print/display text sourced from all-caps enums or free-text admin
// entry with no enforced casing (garment color, category/type/material/style
// names, spot/damage/upcharge labels, alteration names) - matches the
// convention cart-row.tsx already uses on-screen for damages
// (`upperFirst(damage.toLowerCase())`), just generalized to multi-word text.
export function toDisplayCase(text: string): string {
  return text
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

// Garment colors print as the raw enum ("L_BLUE") unless reformatted. The
// enum is always upper snake case, so this always goes through
// `toDisplayCase` after splitting words - there's no "already correctly
// cased" case to preserve, unlike free-text category names.
export function formatGarmentColor(color: string): string {
  return toDisplayCase(color.replace(/_/g, " "));
}

// Each alteration attached to an item, formatted the same way the cart UI
// already shows it (cart-row.tsx: "+ Alterations - {subCategory} {price}"),
// minus the redundant "Alterations - " prefix - the printed section heading
// (see buildCustomizationLines below) already establishes that context.
// Carries the alteration's own `unitPrice` so it prints in the same
// qty/description/price layout as a base item row (previously this charge
// was only silently folded into the item's overall total via
// alterationLinesTotal below, never shown on the alteration's own line).
export function describeAlterationLines(
  item: Pick<CartItem, "alterationItems">
): ITrayPrintCustomizationLine[] {
  return (item.alterationItems ?? [])
    .map((a): ITrayPrintCustomizationLine | undefined => {
      const parts = [a.alterationSubCategory?.name, a.alterationPrice?.name]
        .filter((p): p is string => Boolean(p))
        .map(toDisplayCase);
      return parts.length > 0
        ? { section: "Alterations", label: parts.join(" "), price: a.unitPrice ?? undefined }
        : undefined;
    })
    .filter((line): line is ITrayPrintCustomizationLine => Boolean(line));
}

// Garment info (type/material/style) only ever described WHAT the item is,
// never anything the cashier added to it - upcharges, spots, damages,
// release notes (the cart's "Instructions" picklist), and alterations were
// captured in the cart but never made it onto any printed receipt (damages
// still didn't, until this change). Returned as a flat list of
// {section, label, price?} entries - printed grouped under one heading per
// section, each as its own "+"-prefixed line, rather than folded into the
// item's own description string or left unlabeled which kind of line it is.
export function buildCustomizationLines(
  item: Pick<
    CartItem,
    "upcharges" | "spots" | "damages" | "instructions" | "alterationItems"
  >
): ITrayPrintCustomizationLine[] {
  const lines: ITrayPrintCustomizationLine[] = [];

  for (const s of item.spots ?? []) {
    const label = s.subSpot?.comment || s.label;
    if (label) lines.push({ section: "Spots", label: toDisplayCase(label) });
  }

  for (const d of item.damages ?? []) {
    if (d.damage) lines.push({ section: "Damages", label: toDisplayCase(d.damage) });
  }

  for (const i of item.instructions ?? []) {
    if (i.instruction) lines.push({ section: "Release Notes", label: i.instruction });
  }

  for (const u of item.upcharges ?? []) {
    const label = u.subUpcharge?.upchargeVariant || u.label;
    if (label) {
      lines.push({
        section: "Upcharges",
        label: toDisplayCase(label),
        price: u.subUpcharge?.price ?? undefined,
      });
    }
  }

  lines.push(...describeAlterationLines(item));

  return lines;
}

// Sum of what each alteration attached to this item costs - was never added
// into any print builder's item price, so alteration charges silently never
// showed up in a printed Sub Total/Total even though they're billed.
export function alterationLinesTotal(
  item: Pick<CartItem, "alterationItems">
): number {
  return (item.alterationItems ?? []).reduce(
    (sum, a) => sum + (a.unitPrice ?? 0),
    0
  );
}

// The single, shared definition of "what this item's printed price is" -
// matches the backend's own gross-total convention (BillServiceImpl's
// per-item discount apportionment, mirrored FE-side in
// pickupReceiptMath.ts's itemTotal). Before this, check-in's builder
// included upcharges/spots but not alterations, reprint's included NEITHER,
// and only pickup's (itemTotal) had the full picture - so the same bill
// could print a different item price depending on which of the three
// screens it was printed from. Every builder should use this one function
// rather than recomputing the sum itself.
export function printedItemPrice(
  item: Pick<CartItem, "unitPrice" | "qty" | "upcharges" | "spots" | "alterationItems">
): number {
  return (
    item.unitPrice * item.qty +
    (item.upcharges ?? []).reduce((sum, u) => sum + (u.subUpcharge?.price || 0), 0) +
    (item.spots ?? []).reduce((sum, s) => sum + (s.subSpot?.price || 0), 0) +
    alterationLinesTotal(item)
  );
}

// Every print-request builder composed the printed customer name as just
// `firstName lastName`, silently dropping middleName even though the
// customer record carries one - this is the single place that joins all
// three parts so a receipt's name matches the full name on file.
export function formatFullName(
  person?: {
    firstName?: string | null;
    middleName?: string | null;
    lastName?: string | null;
  } | null
): string {
  return [person?.firstName, person?.middleName, person?.lastName]
    .filter((part) => part && part.trim().length > 0)
    .join(" ")
    .trim();
}

// The pin tag color/tags stored on a cart session (ICartSession.pinTag) never
// carried the 2-letter alias its color config is assigned in Admin > Pin Tag
// Config (e.g. "MO") - only the hex `color` and the bare numeric `tags`
// ("256"), so a printed pin tag could never read "MO256", only "256". Resolves
// the alias by matching the session's stored hex color against the color
// catalog (IColorPintag[]) already used to assign pin tags in the first place.
export function resolvePinTagAlias(
  colorHex: string | null | undefined,
  colorCatalog: Array<{ color: string; alias: string }> | null | undefined
): string | undefined {
  if (!colorHex) return undefined;
  return colorCatalog?.find((c) => c.color === colorHex)?.alias || undefined;
}

// Customer type (Admin > customer record) as a single printed letter -
// Retail/Business/Employee, per the client's requested format. Takes the
// first entry of `customerTypePref`, matching the convention already used
// to derive a customer's single effective type elsewhere (Lobby.tsx).
export function customerStatusLetter(
  customerTypePref?: string[] | null
): string | undefined {
  switch ((customerTypePref?.[0] || "").toUpperCase()) {
    case "RETAIL":
      return "R";
    case "BUSINESS":
      return "B";
    case "EMPLOYEE":
      return "E";
    default:
      return undefined;
  }
}

// Customer's on-file "Pay At" billing preference as a single printed letter -
// Monthly Charge/Card on File, per the client's requested format. A customer
// can have both a "Counter"/"Anytime" preference and CARD_ON_FILE/
// MONTHLY_CHARGE in the same `customerPayAtPref` array (see create-customer.tsx);
// only the latter two map to a printed letter. Monthly takes priority if a
// customer somehow has both.
export function paymentStatusLetter(
  customerPayAtPref?: string[] | null
): string | undefined {
  const prefs = customerPayAtPref ?? [];
  if (prefs.includes("MONTHLY_CHARGE")) return "M";
  if (prefs.includes("CARD_ON_FILE")) return "C";
  return undefined;
}

// LATER/MONTHLY both submit the full bill amount as the "payment" so the
// backend can track it as a deferred, NOT_CLEARED charge (see payment.tsx's
// LaterSection/MonthlySection) - nothing was actually collected from the
// customer. Treating that recorded amount as genuinely "paid" on a printed
// receipt made every deferred receipt's balance print as $0, looking fully
// settled when the whole point of the transaction was that it wasn't.
export function isDeferredPayment(paymentType?: string): boolean {
  return (
    paymentType === PaymentTypeEnum.LATER ||
    paymentType === PaymentTypeEnum.MONTHLY
  );
}
